import { GridSortModel } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useGroupVisitList } from "@/queries/group-visit/use-group-visit-list";
import { DEF_PAGINATION, DGT_Pagination } from "@/utils/utils";
import {
  GROUP_LIST_DEFAULT_SORT_BY,
  GROUP_LIST_DEFAULT_SORT_DIRECTION,
  GROUP_LIST_FILTER,
  GROUP_LIST_SORT_FIELD_MAP,
} from "../group-visit-constant";
import { GroupDetail, GroupListFilter } from "../group-visit-types";
import { toGroupDetail, toGroupListParams } from "../group-visit-utils";

export type UseGroupList = {
  groups: GroupDetail[];
  totalCount: number;
  isLoading: boolean;
  searchKey: string;
  setSearchKey: (value: string) => void;
  statusFilter: GroupListFilter;
  setStatusFilter: (value: GroupListFilter) => void;
  pagination: DGT_Pagination;
  setPagination: (value: DGT_Pagination) => void;
  sortModel: GridSortModel;
  setSortModel: (value: GridSortModel) => void;
  /** Names on the loaded page, for early duplicate feedback on the form. */
  existingGroupNames: string[];
};

/**
 * Group list state. Search, status and archive filtering and paging are all
 * server-side; the archive filter is a flag rather than a status.
 *
 * @author Rajkumar Rathod
 */
export const useGroupList = (): UseGroupList => {
  const [debouncedSearch, setSearchKey, searchKey] = useDebounce("");
  const [statusFilter, setStatusFilter] = useState<GroupListFilter>(
    GROUP_LIST_FILTER.ALL
  );
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const { groupStatus, archive } = toGroupListParams(statusFilter);
  const [sort] = sortModel;

  const { data, isLoading } = useGroupVisitList({
    pagination,
    searchKey: debouncedSearch.trim() || undefined,
    groupStatus,
    archive,
    sortBy: sort
      ? (GROUP_LIST_SORT_FIELD_MAP[sort.field] ?? sort.field)
      : GROUP_LIST_DEFAULT_SORT_BY,
    sortDirection: sort?.sort ?? GROUP_LIST_DEFAULT_SORT_DIRECTION,
  });

  const groups = useMemo(
    () => (data?.data?.content ?? []).map(toGroupDetail),
    [data]
  );

  const existingGroupNames = useMemo(
    () => groups.map((group) => group.groupName ?? "").filter(Boolean),
    [groups]
  );

  // A tab switch reloads from the first page rather than an out-of-range one.
  const changeStatusFilter = useCallback((value: GroupListFilter) => {
    setStatusFilter(value);
    setPagination((current) => ({ ...current, page: 0 }));
  }, []);

  return {
    groups,
    totalCount: data?.data?.page?.totalElements ?? 0,
    isLoading,
    searchKey,
    setSearchKey,
    statusFilter,
    setStatusFilter: changeStatusFilter,
    pagination,
    setPagination,
    sortModel,
    setSortModel,
    existingGroupNames,
  };
};
